import { Subject } from 'rxjs';

import { CorrelatedLogger } from '@eluve/logger';

import type { MuteDetector } from '../MuteDetector';
import { MuteDetectionPipeline } from '../mute-detection-pipeline';
import type { MsgFromMain } from '../workers/messages-types';

/**
 * Configuration for creating an AudioSource
 */
export interface AudioSourceConfig {
  mediaStream: MediaStream;
  worker: Worker;
  logger: CorrelatedLogger;
  MuteDetector: typeof MuteDetector;
  deviceId?: string;
  channelCount: number;
  onMute: () => void;
  onUnmute: () => void;
  onBytesReceived: (bytes: number) => void;
  audioPacketSubject: Subject<{ data: Blob; timestamp: number }>;
}

/**
 * Result of initializing an audio source
 */
export interface AudioSourceInitResult {
  sampleRate: number;
  channels: 1 | 2;
}

/**
 * Abstract base class for audio sources.
 * Handles capturing audio from MediaStream and sending to worker.
 * Implementations handle mode-specific details (PCM vs pre-encoded blobs).
 */
export abstract class AudioSource {
  protected mediaStream: MediaStream;
  protected worker: Worker;
  protected logger: CorrelatedLogger;
  protected MuteDetector: typeof MuteDetector;
  protected deviceId?: string;
  protected channelCount: number;
  protected onMute: () => void;
  protected onUnmute: () => void;
  protected onBytesReceived: (bytes: number) => void;
  protected audioPacketSubject: Subject<{ data: Blob; timestamp: number }>;

  protected muteDetectionPipeline: MuteDetectionPipeline | null = null;
  protected audioContext: AudioContext | null = null;

  constructor(config: AudioSourceConfig) {
    this.mediaStream = config.mediaStream;
    this.worker = config.worker;
    this.logger = config.logger;
    this.MuteDetector = config.MuteDetector;
    this.deviceId = config.deviceId;
    this.channelCount = config.channelCount;
    this.onMute = config.onMute;
    this.onUnmute = config.onUnmute;
    this.onBytesReceived = config.onBytesReceived;
    this.audioPacketSubject = config.audioPacketSubject;
  }

  /**
   * Initialize the audio source and start capturing
   * @returns Sample rate and channel count for worker initialization
   */
  abstract initialize(): Promise<AudioSourceInitResult>;

  /**
   * Stop capturing and clean up resources
   */
  abstract stop(): Promise<void>;

  /**
   * Get the encoding mode for this audio source
   */
  abstract getMode(): 'pcm' | 'blob';

  /**
   * Set up mute detection using MuteDetectionPipeline
   * @param audioContext Optional AudioContext to reuse; if not provided, pipeline creates its own
   */
  protected setupMuteDetection(audioContext?: AudioContext): void {
    try {
      this.muteDetectionPipeline = new MuteDetectionPipeline({
        mediaStream: this.mediaStream,
        audioContext,
        MuteDetector: this.MuteDetector,
        onMute: this.onMute,
        onUnmute: this.onUnmute,
        logger: this.logger,
      });
      this.muteDetectionPipeline.start();
    } catch (error) {
      this.logger(
        'warn',
        'Failed to initialize mute detection, continuing without it',
        {
          error,
        },
      );
    }
  }

  /**
   * Clean up mute detection resources
   */
  protected async cleanupMuteDetection(): Promise<void> {
    if (this.muteDetectionPipeline) {
      await this.muteDetectionPipeline.stop();
      this.muteDetectionPipeline = null;
    }
  }

  /**
   * Send a message to the worker
   */
  protected postMessage(message: MsgFromMain, transfer?: Transferable[]): void {
    if (transfer) {
      this.worker.postMessage(message, transfer);
    } else {
      this.worker.postMessage(message);
    }
  }
}
